import React, { useCallback, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import BulkSportAllocationModal from 'Components/sports/allocations/BulkSportAllocationModal';
import EventTagCell from 'Components/sports/allocations/EventTagCell';

import CommitmentLayerControl, { COMMITMENT_LAYERS } from './CommitmentLayerControl';
import LineTypeIcon from './LineTypeIcon';
import useREColumns from './useREColumns';

import { formatFigure, formatQtyDisplay as formatQty } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import ExpandCollapseAllButton from 'CommonComponents/ExpandCollapseAllButton';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';

import BoxIcon from 'Assets/icons/box.svg';
import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import CircleHelpIconUrl from 'Assets/icons/circle-help.svg?url';

// Logical (RTL-safe) indent classes as string literals so Tailwind keeps them.
// The expenditure tree nests six deep — worknonwork → department → cost-centre
// → project → template → line — plus a seventh level for a line's sub-rows
// (pack days, committed/uncommitted). The scale must cover all of them or every
// level past the ceiling collapses onto the same indent and a project, its
// template and that template's lines render indistinguishably (IB-878 / R4).
const INDENT_CLASS = ['tw-ps-0', 'tw-ps-3', 'tw-ps-6', 'tw-ps-9', 'tw-ps-12', 'tw-ps-16', 'tw-ps-20', 'tw-ps-24'];

const indentClass = (depth) => INDENT_CLASS[Math.min(depth, INDENT_CLASS.length - 1)];

// Cash / VIK have no value under the committed / uncommitted layers (only the
// total splits), so they render blank like any other missing figure.
const NO_FIGURE = '';

// Qty shows integers as-is and fractions to one decimal place.
const formatQtyFigure = (qty) => formatFigure(qty, formatQty, { precision: 1 });

/**
 * The figures a node exposes under the active commitment layer. Committed and
 * uncommitted re-base Total + Qty to the node's own split and blank the funding
 * columns (cash/vik → blank); total and split's main row show funding as usual.
 */
function nodeLayerFigures(node, layer) {
    // The unallocated sub-row is an allocation concept, not a commitment one: it
    // always shows its full, non-re-based total and never blanks its funding columns, in every
    // layer mode (it is also excluded from the commitment roll-ups server-side).
    const isUnallocated = node.meta?.sub_row_kind === 'unallocated';

    if (!isUnallocated && layer === COMMITMENT_LAYERS.COMMITTED) {
        return { total: node.committed, qty: node.committed_qty, moneyDash: true };
    }
    if (!isUnallocated && layer === COMMITMENT_LAYERS.UNCOMMITTED) {
        return { total: node.uncommitted, qty: node.uncommitted_qty, moneyDash: true };
    }
    return { total: node.total, cash: node.cash, vik: node.vik, qty: node.qty, moneyDash: false };
}

/** Flat, de-duplicated list of bulk-selectable line refs across the canonical tree. */
function flattenSelectableRefs(nodes) {
    const refs = [];
    const seen = new Set();
    const walk = (list) => {
        (list || []).forEach((node) => {
            // Sub-rows are a breakdown of the line above them (pack days, the
            // committed / uncommitted split) — never a bulk-allocation target in
            // their own right, whatever the payload claims.
            if (node.row_type !== 'sub_row' && node.selectable && node.meta?.id != null && node.meta?.allocatable_type) {
                // Thread the line's declared quantity and an expense-type flag so
                // the bulk modal can offer (and gate) the by-quantity split, which
                // is valid only for a single Expense line carrying a quantity.
                //
                // node.qty is deliberately the TOTAL-layer quantity, not
                // nodeLayerFigures(node, layer).qty — the backend's by-quantity
                // basis (venueDeclaredQty) is always the line's full declared
                // quantity regardless of which commitment layer the table is
                // currently displaying, so the modal's "Qty (of N)" must quote
                // the same total the backend will validate Apply against.
                const ref = {
                    type: node.meta.allocatable_type,
                    id: node.meta.id,
                    name: node.name,
                    qty: node.qty != null ? Number(node.qty) : null,
                    is_expense: node.meta.allocatable_type === 'expense',
                    is_manual_override: (node.allocations || []).some((a) => a.is_manual_override === true),
                };
                const key = `${ref.type}-${ref.id}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    refs.push(ref);
                }
            }
            if (node.children && node.children.length) walk(node.children);
        });
    };
    walk(nodes);
    return refs;
}

/**
 * Keep a level-0 node if its name matches the query, or any descendant leaf does.
 *
 * With `pinGroupHeaders`, a level-0 GROUP survives a search that matches none of
 * its lines: it renders as a heading with no children, which rebaseTotals then
 * re-bases to zero. The alternative — dropping it — hides the fact that the
 * group exists and leaves the remaining heading looking like the whole table.
 *
 * Returns the caller's own array by identity when the query is blank, so a
 * blank search never triggers the re-basing path below.
 */
function filterNodes(nodes, search, pinGroupHeaders = false) {
    const q = (search || '').toLocaleLowerCase();
    if (!q) return nodes;

    const keep = (node) => {
        const nameMatch = (node.name || '').toLocaleLowerCase().includes(q);
        if (!node.children || !node.children.length) {
            return nameMatch ? node : null;
        }
        if (nameMatch) return node;
        const keptChildren = node.children.map(keep).filter(Boolean);
        return keptChildren.length ? { ...node, children: keptChildren } : null;
    };

    return (nodes || [])
        .map((node) => {
            const kept = keep(node);
            if (kept) return kept;
            return pinGroupHeaders && node.row_type === 'group' ? { ...node, children: [] } : null;
        })
        .filter(Boolean);
}

/**
 * Graft lazily-fetched children into a summary tree. A group whose children
 * were fetched takes them in place of its summary children and stops being
 * lazy; the fetched children are merged in turn, so a cost centre expanded
 * inside a fetched department keeps its own lines.
 *
 * Returns the caller's own array when nothing was fetched.
 */
function mergeLoaded(nodes, loaded) {
    if (!nodes || !Object.keys(loaded).length) return nodes;

    return nodes.map((node) => {
        const fetched = loaded[node.key];
        const children = fetched ?? node.children;
        if (!fetched && !(children && children.length)) return node;

        return {
            ...node,
            children: mergeLoaded(children, loaded),
            meta: fetched ? { ...(node.meta || {}), children_lazy: false } : node.meta,
        };
    });
}

/**
 * A row's expanded state. With `ctx.expansion` (the lazily-loaded event view)
 * the state lives in the table, keyed by node key, so it survives the row
 * unmounting under a search, its clearing, or a lazy load; a key the user
 * never toggled falls back to the row's own default. Without it the row keeps
 * its own state, as it always has.
 */
function useRowExpanded(node, ctx, localExpanded, setLocalExpanded) {
    const shared = ctx.expansion;
    const expanded = shared && node.key in shared.keys ? shared.keys[node.key] : localExpanded;
    const toggle = () => (shared ? shared.set(node.key, !expanded) : setLocalExpanded((s) => !s));

    return [expanded, toggle];
}

/** Two-decimal round used wherever a client-side roll-up replaces a server figure. */
const round2 = (value) => (value == null ? null : Math.round(value * 100) / 100);

/**
 * Re-base every group's figures onto the descendants that survived the search
 * filter, returning `{nodes, agg}` — the rewritten tree and the table-level
 * aggregate the footer reads.
 *
 * The contribution rule mirrors the server presenter exactly, or the figures
 * jump the moment a user types into the search box:
 *   - cash / vik / total   ← `line` nodes, PLUS a group-level `unallocated`
 *     sub_row (a project's own remainder, which its group's cached total
 *     already carries).
 *   - committed / uncommitted ← `line` nodes only. Sub-rows never carry a
 *     commitment figure, so the split legitimately falls short of the total by
 *     the unallocated amount; both stay null when no descendant carries one.
 *
 * A line's own children only split the line, so they are never walked again.
 */
function rebaseTotals(nodes) {
    const agg = { cash: 0, vik: 0, total: 0, committed: null, uncommitted: null };

    const add = (cash, vik, total, committed, uncommitted) => {
        agg.cash += cash || 0;
        agg.vik += vik || 0;
        agg.total += total || 0;
        if (committed != null) agg.committed = (agg.committed ?? 0) + committed;
        if (uncommitted != null) agg.uncommitted = (agg.uncommitted ?? 0) + uncommitted;
    };

    const out = (nodes || []).map((node) => {
        if (node.row_type === 'group') {
            const child = rebaseTotals(node.children || []);
            add(child.agg.cash, child.agg.vik, child.agg.total, child.agg.committed, child.agg.uncommitted);
            return {
                ...node,
                // A figure the node never carried stays absent: SportRETreePresenter
                // emits null cash/vik on every group while those columns are shown,
                // so re-basing them would make blank cells sprout numbers mid-search.
                cash: node.cash == null ? null : round2(child.agg.cash),
                vik: node.vik == null ? null : round2(child.agg.vik),
                total: node.total == null ? null : round2(child.agg.total),
                // A group that DOES carry a split keeps one: with no matching line
                // beneath it the split is zero, not blank, so a zero-line heading
                // reads the same under every commitment layer as it does unsearched.
                committed: node.committed == null ? null : round2(child.agg.committed ?? 0),
                uncommitted: node.uncommitted == null ? null : round2(child.agg.uncommitted ?? 0),
                children: child.nodes,
            };
        }

        if (node.row_type === 'line') {
            add(node.cash, node.vik, node.total, node.committed, node.uncommitted);
            return node;
        }

        if (node.meta?.sub_row_kind === 'unallocated') {
            add(node.cash, node.vik, node.total, null, null);
        }
        return node;
    });

    return { nodes: out, agg };
}

/**
 * True when a node's children are all sub-rows — pack days, or the inline
 * committed / uncommitted split. Such a node is a CONTAINER LEAF: one countable
 * row for the pager, never something a page boundary may cut in half.
 *
 * Without this, a 12-day pack counts as 12 leaves and `paginateNodes` can end a
 * page mid-pack, rendering the container with only some of its days. The same
 * miscount already applied to a template child carrying committed + uncommitted
 * sub-rows (it counted 2), so both cases are handled here.
 */
function isContainerLeaf(node, workforceEnabled = true) {
    if (node.row_type === 'line' && !!node.children && node.children.length > 0 && node.children.every((child) => child.row_type === 'sub_row')) {
        return true;
    }

    // A blocked Workforce group can never expand — GroupRow suppresses its
    // chevron and children for a user without the workforce module — so its
    // position leaves must not inflate the pager past what that user can
    // actually page through. Counting the (collapsed, unexpandable) group as
    // one leaf mirrors how a container leaf's own children never count
    // individually (PR #1105 review).
    if (!workforceEnabled && node.name === 'Workforce') {
        return true;
    }

    return false;
}

/**
 * A childless GROUP is table structure, not a row of data: the server's line
 * count never counted it, so counting it here would shift every page boundary
 * by one against the pager total. Applies equally to countLeaves and
 * paginateNodes, which must stay in lockstep.
 *
 * A group stamped `meta.children_lazy` (Sport tab summary) is childless only
 * because its rows haven't been fetched yet, not because it's empty — it must
 * never read as structural, or every lazy group renders as a bare heading and
 * the whole table looks empty until something is expanded. The Venue tab
 * never sets `children_lazy`, so this doesn't change its pagination.
 */
function isStructuralGroup(node) {
    if (node.meta?.children_lazy === true) return false;
    return node.row_type === 'group' && (!node.children || node.children.length === 0);
}

/**
 * Window the tree to a page of leaves. Leaves are counted in tree order; a group
 * survives only if it retains at least one leaf within [from, to]. Preserves the
 * nesting so groups still render around their kept leaves.
 *
 * With `pinGroupHeaders`, a LEVEL-0 group is always kept, carrying whatever
 * children survived the window (possibly none) — the group structure is never
 * paginated away, so both headings and their totals stay on every page. It is
 * stamped `meta.paged_out` when the window took all its lines, which is what
 * separates "has lines, none here" from a genuinely empty group. Nested groups
 * (department / cost-centre / project / template) keep the drop-when-empty
 * rule; pinning those too would render the venue's whole skeleton on page 1.
 *
 * Index accounting is untouched by pinning: a node still consumes exactly the
 * index it did before, including the blocked-Workforce container leaf, so
 * countLeaves and this function stay in lockstep. Pinning changes only what
 * renders.
 *
 * A group-level `sub_row` (e.g. a project's Unallocated row — see
 * VenueBudgetService, stamped `is_sub_row`/`sub_row_kind: 'unallocated'` as a
 * sibling of the project's own line children, not nested under a line) is never
 * a countable pager leaf: it mirrors VenueRETreePresenter::countLines, which
 * only counts `row_type === 'line'`. It is never windowed by [from, to] either
 * — the backend total it's derived from was never counted, so there is no page
 * it belongs to. It travels with the rows it breaks down, which splits by
 * whether those rows exist at all: a level that owns pageable rows but kept
 * none on this page drops its sub-rows (they reappear on the page its rows land
 * on, and its group falls through to `pagedOut`), while a level whose only
 * members ARE sub-rows keeps them on every page its group renders — there is no
 * page for them to wait for, and dropping them would leave their amount in the
 * heading total with nothing on screen to account for it.
 */
function paginateNodes(nodes, from, to, workforceEnabled = true, pinGroupHeaders = false) {
    let index = 0;

    const pinned = (node, depth) => pinGroupHeaders && depth === 0 && node.row_type === 'group';
    const pagedOut = (node) => ({ ...node, children: [], meta: { ...(node.meta || {}), paged_out: true } });

    const walk = (list, depth) => {
        const rows = list || [];
        const out = [];
        // Rows kept at THIS level that are not sub-rows. Sub-rows keep their
        // position in `out` while it counts, so nothing is reordered.
        let kept = 0;
        // Whether this level owns anything a page boundary could take away —
        // sub-rows never are, and neither is a nested structural group, which is
        // dropped below rather than windowed. A level with nothing pageable (a
        // project whose lines all sit on other venues, leaving its remainder as
        // the whole of its content) has no page to wait for, so its sub-rows
        // render wherever it renders.
        const pageable = rows.some((node) => node.row_type !== 'sub_row' && !(depth > 0 && isStructuralGroup(node)));

        rows.forEach((node) => {
            if (node.row_type === 'sub_row') {
                out.push(node);
            } else if (isStructuralGroup(node)) {
                // Heading only, with no row of its own to place on a page. At
                // level 0 that is the table's structure and travels with every
                // page; deeper down it is noise repeated on all of them.
                if (depth === 0) {
                    out.push(node);
                    kept += 1;
                }
            } else if (node.children && node.children.length && !isContainerLeaf(node, workforceEnabled)) {
                const keptChildren = walk(node.children, depth + 1);
                if (keptChildren.length) {
                    out.push({ ...node, children: keptChildren });
                    kept += 1;
                } else if (pinned(node, depth)) {
                    out.push(pagedOut(node));
                    kept += 1;
                }
            } else {
                // A container leaf is kept whole — its sub-rows travel with it.
                index += 1;
                if (index >= from && index <= to) {
                    out.push(node);
                    kept += 1;
                } else if (pinned(node, depth)) {
                    out.push(pagedOut(node));
                    kept += 1;
                }
            }
        });

        return kept || !pageable ? out : [];
    };
    return walk(nodes, 0);
}

/** Count leaf lines in a tree (used to size the pager against a search-narrowed set). */
function countLeaves(nodes, workforceEnabled = true) {
    let count = 0;
    const walk = (list) => {
        (list || []).forEach((node) => {
            if (node.row_type === 'sub_row' || isStructuralGroup(node)) {
                // Not a pager leaf — see paginateNodes.
                return;
            }
            if (node.children && node.children.length && !isContainerLeaf(node, workforceEnabled)) {
                walk(node.children);
            } else {
                count += 1;
            }
        });
    };
    walk(nodes);
    return count;
}

export default function RevenuesExpendituresTable({
    title,
    context = 'venue',
    eventId = null,
    nodes = [],
    totals = null,
    loading = false,
    vikEnabled = false,
    workforceEnabled = true,
    sports = [],
    sportAllocationStoreUrl = null,
    dayTagStoreUrl = null,
    dayTagBulkUrl = null,
    bulkPreviewUrl = null,
    bulkApplyUrl = null,
    venueId = null,
    onAllocationUpdated = null,
    paginate = false,
    totalCount = 0,
    pinGroupHeaders = false,
    defaultExpandLevelZero = false,
    commitmentLayerEnabled = false,
    committedTotals = null,
    isRevenue = false,
    // Summary-first loading (event view). All three are optional: without them
    // `nodes` is the whole tree and search stays client-side.
    //   loadChildren(node) → Promise<children> for a group stamped meta.children_lazy
    //   onSearch(q, signal) → Promise<nodes> for the side, already narrowed to q
    //   loadAll() → Promise<nodes>, the whole side in full detail (Expand all)
    loadChildren = null,
    onSearch = null,
    loadAll = null,
}) {
    const [search, setSearch] = useState('');
    const [allExpanded, setAllExpanded] = useState(false);
    const [expandKey, setExpandKey] = useState(0);
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    // In-memory only — resets on reload (IB-654 convention).
    const [layer, setLayer] = useState(COMMITMENT_LAYERS.TOTAL);
    const activeLayer = commitmentLayerEnabled ? layer : COMMITMENT_LAYERS.TOTAL;

    const bulkModal = useModal();
    const { columns, toggle: toggleColumn, definitions } = useREColumns(context, eventId);

    // Lazily-fetched children keyed by the parent group's key, and each pending
    // or failed fetch's status ('loading' | 'error').
    const [loadedChildren, setLoadedChildren] = useState({});
    const [childStatus, setChildStatus] = useState({});
    // The whole side, fetched once by Expand all; it supersedes the summary.
    const [fullNodes, setFullNodes] = useState(null);
    const [expandAllStatus, setExpandAllStatus] = useState(null);
    const [searchResult, setSearchResult] = useState(null);
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchAttempt, setSearchAttempt] = useState(0);
    // Row expansion, held here only on the lazily-loaded event view (see useRowExpanded).
    const [expandedKeys, setExpandedKeys] = useState({});
    const expansion = useMemo(
        () => (typeof loadChildren === 'function' ? { keys: expandedKeys, set: (key, value) => setExpandedKeys((prev) => ({ ...prev, [key]: value })) } : null),
        [loadChildren, expandedKeys],
    );

    const requestChildren = useCallback(
        (node) => {
            if (typeof loadChildren !== 'function') return;
            setChildStatus((prev) => ({ ...prev, [node.key]: 'loading' }));
            loadChildren(node)
                .then((children) => {
                    setLoadedChildren((prev) => ({ ...prev, [node.key]: children || [] }));
                    setChildStatus((prev) => {
                        const next = { ...prev };
                        delete next[node.key];
                        return next;
                    });
                })
                .catch(() => setChildStatus((prev) => ({ ...prev, [node.key]: 'error' })));
        },
        [loadChildren],
    );

    const treeNodes = useMemo(() => fullNodes ?? mergeLoaded(nodes, loadedChildren), [fullNodes, nodes, loadedChildren]);

    // Once Expand all has fetched the whole side, search filters it locally.
    // Where search can reach the server (the event view), the query is trimmed
    // everywhere it is applied, so a whitespace-only query is no query at all
    // and the client filter agrees with what the server matched. The venue view
    // keeps filtering on the raw input, as it always has.
    const serverSearch = typeof onSearch === 'function' && !fullNodes;
    const serverQuery = search.trim();
    const activeSearch = typeof onSearch === 'function' ? serverQuery : search;
    const searchReady = serverSearch && !!serverQuery && searchResult?.q === serverQuery;
    const searchPending = serverSearch && !!serverQuery && !searchReady && searchStatus !== 'error';

    useEffect(() => {
        if (!serverSearch || !serverQuery || searchResult?.q === serverQuery) {
            setSearchStatus(null);
            return undefined;
        }

        const controller = new AbortController();
        setSearchStatus(null);
        const timer = setTimeout(() => {
            setSearchStatus('loading');
            onSearch(serverQuery, controller.signal)
                .then((result) => {
                    setSearchResult({ q: serverQuery, nodes: result || [] });
                    setSearchStatus(null);
                })
                .catch(() => {
                    if (!controller.signal.aborted) setSearchStatus('error');
                });
        }, 300);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [serverQuery, serverSearch, searchAttempt]);

    // While a server search is active its result is the side's tree; clearing
    // the search falls back to the summary plus whatever was already expanded.
    const sourceNodes = serverSearch && serverQuery ? (searchReady ? searchResult.nodes : []) : treeNodes;
    const bodyLoading = loading || searchPending || expandAllStatus === 'loading';

    // Filter first: search hides rows, and "Select all" must never reach past the
    // currently-visible set into hidden lines.
    const filteredNodes = useMemo(() => filterNodes(sourceNodes, activeSearch, pinGroupHeaders), [sourceNodes, activeSearch, pinGroupHeaders]);

    // Under a search, group headings and the footer must quote the set actually
    // on screen, so they are re-based off the surviving lines. With no search
    // the server's figures stay authoritative: filterNodes hands back the
    // caller's own array, and a client-side float roll-up would drift by pennies
    // against the same totals the summary cards render.
    const { nodes: displayNodes, agg: searchAgg } = useMemo(
        () => (activeSearch ? rebaseTotals(filteredNodes) : { nodes: filteredNodes, agg: null }),
        [filteredNodes, activeSearch],
    );

    const pagination = usePagination({ perPage: 50 });
    const { to, from, total, lastPage, perPage, page } = pagination;

    // While searching, page against the filtered result set rather than the server's
    // unfiltered total — otherwise a later page can render empty while the pager still
    // claims more rows exist.
    const effectiveTotalCount = useMemo(
        () => (activeSearch ? countLeaves(filteredNodes, workforceEnabled) : totalCount),
        [activeSearch, filteredNodes, totalCount, workforceEnabled],
    );

    useEffect(() => {
        if (!paginate) return;
        const pageCount = Math.ceil(effectiveTotalCount / perPage) || 1;
        // Switching to a smaller venue leaves `page` past the end of the new row
        // set, and react-paginate cannot correct it — with a page count of 1 and
        // forcePage 2 it never fires a change — so the window clamps itself onto
        // the LAST VALID page, not page 1: shrinking from page 7 onto 45 rows
        // lands on 5. Recognise the unclamped state by a `to` below `from`; no
        // row satisfies that pair, so every group renders paged-out above an
        // empty-table notice. The page COUNT is untouched — still the full row
        // set, whatever is expanded.
        //
        // Safe only while the row count never dips to zero mid-refetch: the
        // request hook holds the previous `data` until a response replaces it,
        // so a refetch leaves `totalCount` alone and this stays a no-op. Clear
        // `data` when a request starts and it throws the user back a page on
        // every event-tag edit, each of which refetches.
        //
        // The stale window still reaches a render, just never a painted one:
        // the request hook's `setData` and `setLoading(false)` are separate
        // unbatched renders, and the one pairing new rows with the old window
        // still has `loading` true, so the body is a skeleton. Automatic
        // batching (React 18) merges those two renders and paints it.
        const currentPage = Math.min(page, pageCount);
        pagination.setPage(currentPage);
        pagination.setFrom(perPage * currentPage - perPage + 1);
        pagination.setTo(Math.min(perPage * currentPage, effectiveTotalCount));
        pagination.setLastPage(pageCount);
        pagination.setTotal(effectiveTotalCount);
    }, [page, perPage, effectiveTotalCount, paginate]);

    // Reset to page 1 whenever the search term changes so a stale later page doesn't
    // shadow the newly-filtered results.
    useEffect(() => {
        if (paginate) pagination.setPage(1);
    }, [search]);

    const handleToggleAll = () => {
        // A summary tree holds no lines, so expanding it all first fetches the
        // whole side once. `expandAllStatus !== 'loading'` keeps a second click
        // while that fetch is in flight from firing a duplicate one.
        if (!allExpanded && typeof loadAll === 'function' && !fullNodes && expandAllStatus !== 'loading') {
            setExpandAllStatus('loading');
            loadAll()
                .then((full) => {
                    setFullNodes(full || []);
                    setExpandAllStatus(null);
                    setExpandedKeys({});
                    setAllExpanded(true);
                    setExpandKey((k) => k + 1);
                })
                .catch(() => setExpandAllStatus('error'));
            return;
        }

        setExpandedKeys({});
        setAllExpanded((prev) => !prev);
        setExpandKey((k) => k + 1);
    };

    // Event-view revenue lines carry no quantity and no cash/VIK breakdown, so
    // those columns would render empty — suppress them regardless of the stored
    // (expenditure-shared) column toggles. Venue revenue lines carry their qty.
    const suppressQty = isRevenue && context === 'event';
    const suppressFunding = isRevenue && context === 'event';

    const showSource = context === 'event' && !!columns.source;
    const showDescription = context === 'venue' && !!columns.description;
    const showPrice = context === 'venue' && !!columns.price;
    const showQty = !!columns.qty && !suppressQty;
    const showCash = !!columns.cash && !suppressFunding;
    const showVik = !!columns.vik && !!vikEnabled && !suppressFunding;

    const allLineRefs = useMemo(() => flattenSelectableRefs(filteredNodes), [filteredNodes]);
    const canBulkEdit = context === 'venue' && !!venueId && !!bulkPreviewUrl && !!bulkApplyUrl && sports.length > 0 && allLineRefs.length > 0;

    const lineKey = (type, id) => `${type}-${id}`;

    const toggleLine = useCallback((type, id) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            const key = lineKey(type, id);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const isLineSelected = useCallback((type, id) => selectedKeys.has(lineKey(type, id)), [selectedKeys]);

    const allVisibleSelected = allLineRefs.length > 0 && allLineRefs.every((l) => selectedKeys.has(lineKey(l.type, l.id)));
    const someVisibleSelected = allLineRefs.some((l) => selectedKeys.has(lineKey(l.type, l.id)));

    const toggleAllVisible = () => {
        setSelectedKeys((prev) => {
            if (allVisibleSelected) return new Set();
            const next = new Set(prev);
            allLineRefs.forEach((l) => next.add(lineKey(l.type, l.id)));
            return next;
        });
    };

    const selectedLineRefs = useMemo(() => allLineRefs.filter((l) => selectedKeys.has(lineKey(l.type, l.id))), [allLineRefs, selectedKeys]);

    // Paginate the already-filtered tree for rendering (venue revenues only).
    //
    // The page set is sized from the full line count, never from what happens to
    // be expanded: collapsing a group is local GroupRow state that never reaches
    // the pager, so page boundaries stay put as the user expands and collapses.
    const visibleNodes = useMemo(() => {
        if (!paginate || !totalCount) return displayNodes;
        return paginateNodes(displayNodes, from, to, workforceEnabled, pinGroupHeaders);
    }, [displayNodes, paginate, totalCount, from, to, workforceEnabled, pinGroupHeaders]);

    // Pinned headings survive a query that matches nothing, so an empty result
    // no longer means an empty node list. Nothing but bare headings is the
    // empty case; the notice renders UNDER them, keeping both the group
    // structure and an explicit "nothing matched".
    const loadFailed = expandAllStatus === 'error' || (serverSearch && !!serverQuery && searchStatus === 'error');
    const showEmptyState = !bodyLoading && !loadFailed && (visibleNodes ?? []).every(isStructuralGroup);

    // Count of always-present columns for the empty / skeleton colspan.
    const colSpan =
        (canBulkEdit ? 1 : 0) +
        1 +
        (showSource ? 1 : 0) +
        (showDescription ? 1 : 0) +
        (showPrice ? 1 : 0) +
        (showQty ? 1 : 0) +
        (showCash ? 1 : 0) +
        (showVik ? 1 : 0) +
        1 +
        1;

    const trailingHeader = context === 'venue' ? $t('Event') : $t('% of line');

    // Under a search the footer reads the re-based aggregate, so it reconciles
    // with the group headings above it rather than still quoting the whole venue.
    const effectiveTotals = searchAgg ? { cash: round2(searchAgg.cash), vik: round2(searchAgg.vik), total: round2(searchAgg.total) } : totals;
    // Only ever a re-based version of a split the server actually sent — a table
    // given no committed_totals stays without one under search too, rather than
    // growing a footer split out of the client-side roll-up.
    const effectiveCommittedTotals =
        searchAgg && committedTotals ? { committed: round2(searchAgg.committed) ?? 0, uncommitted: round2(searchAgg.uncommitted) ?? 0 } : committedTotals;

    // The footer Total row re-bases with the active commitment layer so it
    // reconciles with the re-based body/group rows (and the Commitment card).
    // Under Committed/Uncommitted the funding cells render blank, as the body does.
    const footerMoneyDash = activeLayer === COMMITMENT_LAYERS.COMMITTED || activeLayer === COMMITMENT_LAYERS.UNCOMMITTED;
    const footerTotal =
        activeLayer === COMMITMENT_LAYERS.COMMITTED
            ? effectiveCommittedTotals?.committed
            : activeLayer === COMMITMENT_LAYERS.UNCOMMITTED
              ? effectiveCommittedTotals?.uncommitted
              : effectiveTotals?.total;

    const ctx = {
        context,
        showSource,
        showDescription,
        showPrice,
        showQty,
        showCash,
        showVik,
        colSpan,
        commitmentLayerEnabled,
        layer: activeLayer,
        vikEnabled,
        workforceEnabled,
        defaultExpandLevelZero,
        sports,
        sportAllocationStoreUrl,
        dayTagStoreUrl,
        dayTagBulkUrl,
        venueId,
        onAllocationUpdated,
        selectable: canBulkEdit,
        isLineSelected,
        toggleLine,
        forceExpanded: allExpanded,
        expandKey,
        requestChildren: typeof loadChildren === 'function' ? requestChildren : null,
        childStatus,
        expansion,
    };

    return (
        <div className="table__card tw-space-y-6">
            <div className="tw-flex tw-justify-between tw-items-center">
                <div>
                    <h4 className="table__header">{title}</h4>
                    <div className="tw-flex tw-items-center tw-space-x-3">
                        {paginate && <PaginationInfo to={to} from={from} total={total} />}
                        {paginate && (
                            <TablePagination
                                lastPage={lastPage}
                                pageChange={(data) => pagination.setPage(+data.selected + 1)}
                                forcePage={pagination.page - 1}
                            />
                        )}
                        <ExpandCollapseAllButton expanded={allExpanded} onClick={handleToggleAll} />
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-x-2.5">
                    {commitmentLayerEnabled && <CommitmentLayerControl value={layer} onChange={setLayer} />}
                    {canBulkEdit && (
                        <Button variant="primary" small onClick={() => bulkModal.show()} disabled={selectedLineRefs.length === 0}>
                            {$t('Apply event split ({{count}} selected)', { count: selectedLineRefs.length })}
                        </Button>
                    )}
                    {paginate && (
                        <PerPageSelect
                            value={perPage}
                            onChange={(e) => {
                                pagination.setPage(1);
                                pagination.setPerPage(parseInt(e.target.value));
                            }}
                        />
                    )}
                    <TableFilterModal noActions headerText={$t('Columns')} contentMaxHeight="none">
                        <div className="tw-space-y-2 tw-py-1">
                            {definitions
                                .filter((def) => def.key !== 'vik' || vikEnabled)
                                .filter((def) => !(suppressQty && def.key === 'qty'))
                                .filter((def) => !(suppressFunding && (def.key === 'cash' || def.key === 'vik')))
                                .map((def) => (
                                    <CheckboxInput key={def.key} label={$t(def.label)} checked={!!columns[def.key]} onChange={() => toggleColumn(def.key)} />
                                ))}
                        </div>
                    </TableFilterModal>
                    <TableSearchInput searchValue={search} handleChange={(e) => setSearch(e.target.value)} />
                </div>
            </div>

            {canBulkEdit && (
                <BulkSportAllocationModal
                    modal={bulkModal}
                    venueId={venueId}
                    sports={sports}
                    selectedLines={selectedLineRefs}
                    bulkPreviewUrl={bulkPreviewUrl}
                    bulkApplyUrl={bulkApplyUrl}
                    onApplied={() => {
                        setSelectedKeys(new Set());
                        if (typeof onAllocationUpdated === 'function') onAllocationUpdated();
                    }}
                />
            )}

            <div className="inbud-table_wrapper">
                <table className="inbud-table inbud-table_first-sticky tw-table-fixed">
                    <thead>
                        <tr>
                            {canBulkEdit && (
                                <th className="tw-w-10">
                                    <CheckboxInput
                                        aria-label={$t('Select all lines')}
                                        checked={allVisibleSelected}
                                        indeterminate={!allVisibleSelected && someVisibleSelected}
                                        onChange={toggleAllVisible}
                                    />
                                </th>
                            )}
                            <th>{$t('Name')}</th>
                            {showSource && <th className="tw-w-48">{$t('Source')}</th>}
                            {showDescription && <th className="tw-w-48">{$t('Description')}</th>}
                            {showPrice && <th className="tw-w-36">{$t('Price')}</th>}
                            {showQty && <th className="tw-w-28">{$t('Qty')}</th>}
                            {showCash && <th className="tw-w-60">{$t('Cash')}</th>}
                            {showVik && (
                                <th className="tw-w-60">
                                    <Tooltip placement="bottom" tooltipContent={$t('tooltip.vik')} tooltipType="table">
                                        <div className="tw-flex tw-justify-center tw-items-center tw-gap-x-1">
                                            <div>{$t('VIK')}</div>
                                            <img src={CircleHelpIconUrl} className="tw-w-3 tw-h-3" />
                                        </div>
                                    </Tooltip>
                                </th>
                            )}
                            <th className="tw-w-60">{$t('Total')}</th>
                            <th className="tw-w-40">{trailingHeader}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bodyLoading && <TableRowSkeleton rows={5} columns={colSpan} />}
                        {!bodyLoading && expandAllStatus === 'error' && <LoadErrorRow ctx={ctx} onRetry={handleToggleAll} />}
                        {!bodyLoading && serverSearch && !!serverQuery && searchStatus === 'error' && (
                            <LoadErrorRow ctx={ctx} onRetry={() => setSearchAttempt((n) => n + 1)} />
                        )}
                        {!bodyLoading && visibleNodes && visibleNodes.map((node) => <RENodeRow key={node.key} node={node} depth={0} ctx={ctx} />)}
                        {showEmptyState && (
                            <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                                <td colSpan={colSpan} className="tw-p-4">
                                    <span>{$t('No data available in table')}</span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {totals && (
                        <tfoot>
                            <tr className="!tw-bg-charcoal">
                                {canBulkEdit && <td></td>}
                                <td>{$t('Total')}</td>
                                {showSource && <td></td>}
                                {showDescription && <td></td>}
                                {showPrice && <td></td>}
                                {showQty && <td></td>}
                                {showCash && <td className="inbud-numeric-cell">{footerMoneyDash ? NO_FIGURE : formatFigure(effectiveTotals.cash)}</td>}
                                {showVik && <td className="inbud-numeric-cell">{footerMoneyDash ? NO_FIGURE : formatFigure(effectiveTotals.vik)}</td>}
                                <td className="inbud-numeric-cell">{formatFigure(footerTotal)}</td>
                                <td></td>
                            </tr>
                            {commitmentLayerEnabled && activeLayer === COMMITMENT_LAYERS.SPLIT && committedTotals && (
                                <>
                                    <FooterCommitmentRow
                                        label={$t('Committed')}
                                        value={effectiveCommittedTotals.committed}
                                        ctx={ctx}
                                        canBulkEdit={canBulkEdit}
                                    />
                                    <FooterCommitmentRow
                                        label={$t('Uncommitted')}
                                        value={effectiveCommittedTotals.uncommitted}
                                        ctx={ctx}
                                        canBulkEdit={canBulkEdit}
                                    />
                                </>
                            )}
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}

/** True when a node's only children are the inline committed/uncommitted split rows. */
function hasOnlyCommitmentSubRows(node) {
    return (
        !!node.children &&
        node.children.length > 0 &&
        node.children.every((child) => child.row_type === 'sub_row' && (child.meta?.sub_row_kind === 'committed' || child.meta?.sub_row_kind === 'uncommitted'))
    );
}

/** Dispatch a canonical node to the correct row renderer. */
function RENodeRow({ node, depth, ctx }) {
    if (node.row_type === 'group') {
        return <GroupRow node={node} depth={depth} ctx={ctx} />;
    }
    if (node.row_type === 'sub_row') {
        // The commitment-layer control supersedes the inline committed/uncommitted
        // split rows; the unallocated sub-row always renders.
        const kind = node.meta?.sub_row_kind;
        if (ctx.commitmentLayerEnabled && (kind === 'committed' || kind === 'uncommitted')) {
            return null;
        }
        // A pack day reached through the generic path (the event view) — the
        // venue view renders the same row from PackLineRow, which additionally
        // threads selection and the live per-day tag control through.
        if (kind === 'pack_day') {
            return <PackDayRow node={node} depth={depth} ctx={ctx} />;
        }
        return <SubRow node={node} depth={depth} ctx={ctx} />;
    }

    // A venue-view pack's Events control is day-tagging's, never TrailingCell's
    // default editable cell — regardless of the active commitment layer. This
    // gate sits ABOVE the layer branches below (was below them, which left the
    // pack-level cell live-and-silently-ignored under Committed / Uncommitted /
    // Split: SplitLineRow/PlainLineRow render TrailingCell, whose write goes to
    // a pack-level SportLineAllocation that buildSportRETree ignores whenever
    // day tags exist). PackLineRow itself suppresses day expansion under a
    // non-Total layer (IB-878 review) — only the venue view owns day-level
    // tagging, so only it needs the pack's bespoke row; on the event view a
    // pack expands through the generic path (keeping the "% of line" trailing
    // column its other lines use).
    if (node.meta?.is_pack && ctx.context === 'venue') {
        return <PackLineRow node={node} depth={depth} ctx={ctx} />;
    }

    // Line. Split reveals the two-figure breakdown under every leaf; the
    // committed / uncommitted layers collapse each leaf to a single re-based row
    // (per-price / per-day detail is a Total-layer view).
    if (ctx.commitmentLayerEnabled && ctx.layer === COMMITMENT_LAYERS.SPLIT) {
        return <SplitLineRow node={node} depth={depth} ctx={ctx} />;
    }
    if (ctx.commitmentLayerEnabled && (ctx.layer === COMMITMENT_LAYERS.COMMITTED || ctx.layer === COMMITMENT_LAYERS.UNCOMMITTED)) {
        return <PlainLineRow node={node} depth={depth} ctx={ctx} />;
    }

    // Total layer (or layer disabled): full detail.
    if (node.children && node.children.length) {
        // With the layer on, the template-child committed/uncommitted children are
        // hidden — render the line flat rather than as an empty expander.
        if (ctx.commitmentLayerEnabled && hasOnlyCommitmentSubRows(node)) {
            return <PlainLineRow node={node} depth={depth} ctx={ctx} />;
        }
        return <ExpandableLineRow node={node} depth={depth} ctx={ctx} />;
    }
    if (Array.isArray(node.meta?.price_rows) && node.meta.price_rows.length) {
        return <PriceRowsLine node={node} depth={depth} ctx={ctx} />;
    }
    return <PlainLineRow node={node} depth={depth} ctx={ctx} />;
}

/** Empty middle cells (source / description / price) for rows that don't carry them. */
function MiddleCells({ ctx, node = null, showValues = false }) {
    return (
        <>
            {ctx.showSource && <td>{showValues && node ? (node.source ?? '') : ''}</td>}
            {ctx.showDescription && <td>{showValues && node ? (node.meta?.description ?? '') : ''}</td>}
            {ctx.showPrice && <td className="inbud-numeric-cell">{showValues && node ? formatFigure(node.meta?.price) : ''}</td>}
        </>
    );
}

function NameCell({ node, depth, expandable = false, expanded = false, onToggle = null, showBox = false, prefix = null, onNameClick = null }) {
    const chevron = expandable ? expanded ? <ChevronUpIcon className="tw-w-2.5 tw-h-2.5" /> : <ChevronDownIcon className="tw-w-2.5 tw-h-2.5" /> : null;

    return (
        <td>
            <div
                className={classNames('tw-flex tw-items-center tw-gap-x-1.5', indentClass(depth), {
                    'tw-cursor-pointer': expandable,
                    'tw-font-semibold': depth === 0 && node.row_type === 'group',
                })}
                onClick={expandable && onToggle ? onToggle : undefined}
            >
                <span className="tw-inline-flex tw-w-4 tw-shrink-0 tw-justify-center">{chevron}</span>
                {/* Sits inside the row's own indent, after the fixed chevron gutter, so a
                    prefixed control (a pack day's select box) never breaks the alignment. */}
                {prefix}
                <LineTypeIcon lineType={node.line_type} />
                {/* onNameClick lets a prefixed checkbox's row (a pack day's select box)
                    make the name text itself part of the clickable target too, without
                    duplicating it inside the checkbox's own label (IB-878 review). */}
                <span className={onNameClick ? 'tw-cursor-pointer' : undefined} onClick={onNameClick || undefined}>
                    {node.name}
                </span>
                {showBox && <BoxIcon className="tw-w-3 tw-h-3 tw-shrink-0" />}
            </div>
        </td>
    );
}

function NumericCells({ node, ctx }) {
    const fig = nodeLayerFigures(node, ctx.layer);

    return (
        <>
            {ctx.showCash && <td className="inbud-numeric-cell">{fig.moneyDash ? NO_FIGURE : formatFigure(node.cash)}</td>}
            {ctx.showVik && <td className="inbud-numeric-cell">{fig.moneyDash ? NO_FIGURE : formatFigure(node.vik)}</td>}
            <td className="inbud-numeric-cell">{formatFigure(fig.total)}</td>
        </>
    );
}

/**
 * Quantity cell. Leaf-only: groups and sub-rows render a blank cell. On the
 * event view a leaf shows "allocated of venue-total" (e.g. 100 of 200); the
 * venue view shows the plain quantity. Renders nothing when the column is off.
 */
function QtyCell({ node, ctx }) {
    if (!ctx.showQty) return null;

    // Pack days are the one sub-row carrying a real quantity of their own (the
    // days a pack was booked for) — every other sub-row splits money only.
    if (node.row_type !== 'line' && node.meta?.sub_row_kind !== 'pack_day') {
        return <td className="inbud-numeric-cell"></td>;
    }

    const { qty } = nodeLayerFigures(node, ctx.layer);
    if (qty == null) {
        return <td className="inbud-numeric-cell"></td>;
    }

    // Figures inside the "x of y" phrase keep their plain form; a lone figure takes the dash rule.
    const content =
        ctx.context === 'event' && node.qty_of != null
            ? $t('{{qty}} of {{total}}', { qty: formatQty(qty), total: formatQty(node.qty_of) })
            : formatQtyFigure(qty);

    return <td className="inbud-numeric-cell">{content}</td>;
}

/** Trailing cell: event-tag chips on the venue view, % of line on the event view. */
function TrailingCell({ node, ctx, showTag = true }) {
    if (ctx.context === 'event') {
        return <td className="inbud-numeric-cell">{formatFigure(node.percentage_of_line, String, { precision: 2, suffix: '%' })}</td>;
    }

    const canTag = showTag && node.meta?.id && ctx.sportAllocationStoreUrl && node.row_type !== 'sub_row';
    return (
        <td>
            {canTag ? (
                <EventTagCell
                    line={{ id: node.meta.id, type: node.meta.allocatable_type, allocations: node.allocations || [] }}
                    sports={ctx.sports}
                    storeUrl={ctx.sportAllocationStoreUrl}
                    onUpdated={ctx.onAllocationUpdated}
                    readonly={node.meta?.readonly === true}
                    venueId={ctx.venueId}
                    allowClear
                />
            ) : null}
        </td>
    );
}

function SelectCheckbox({ node, ctx }) {
    if (!ctx.selectable) return null;
    const canSelect = node.selectable && node.meta?.id != null && node.meta?.allocatable_type;
    const checked = canSelect && ctx.isLineSelected ? ctx.isLineSelected(node.meta.allocatable_type, node.meta.id) : false;
    return (
        <td>
            {canSelect && (
                <CheckboxInput
                    aria-label={$t('Select line')}
                    checked={checked}
                    onChange={() => ctx.toggleLine && ctx.toggleLine(node.meta.allocatable_type, node.meta.id)}
                />
            )}
        </td>
    );
}

function GroupRow({ node, depth, ctx }) {
    const isTicketing = (node.name || '').toLocaleLowerCase() === 'ticketing';
    const isWorkforce = node.name === 'Workforce';
    const workforceBlocked = isWorkforce && !ctx.workforceEnabled;
    const hasChildren = node.children && node.children.length > 0;
    // Pinned onto a page holding none of its own lines (see paginateNodes). It
    // keeps its chevron so the control doesn't flicker in and out as the user
    // pages, and reveals a placeholder instead of the lines that are elsewhere.
    // A group with no lines at all carries no flag and keeps no chevron.
    const pagedOut = node.meta?.paged_out === true;
    const defaultExpanded = ctx.defaultExpandLevelZero && depth === 0 && !isTicketing && !workforceBlocked;

    const [localExpanded, setLocalExpanded] = useState(ctx.expandKey > 0 ? ctx.forceExpanded && !workforceBlocked : defaultExpanded);

    useEffect(() => {
        if (ctx.expandKey > 0) {
            setLocalExpanded(workforceBlocked ? false : ctx.forceExpanded);
        }
    }, [ctx.expandKey]);

    const [expanded, toggleExpanded] = useRowExpanded(node, ctx, localExpanded, setLocalExpanded);

    // A summary group whose children are fetched on first expand.
    const isLazy = node.meta?.children_lazy === true && !!ctx.requestChildren;
    const lazyStatus = ctx.childStatus?.[node.key];

    useEffect(() => {
        if (expanded && isLazy && !lazyStatus) {
            ctx.requestChildren(node);
        }
    }, [expanded, isLazy, lazyStatus]);

    const showChevron = (hasChildren || pagedOut || isLazy) && !workforceBlocked;

    return (
        <>
            <tr className={classNames({ '!tw-bg-blue tw-text-white': depth === 0 })}>
                {ctx.selectable && <td></td>}
                <NameCell node={node} depth={depth} expandable={showChevron} expanded={expanded} onToggle={() => !workforceBlocked && toggleExpanded()} />
                <MiddleCells ctx={ctx} />
                <QtyCell node={node} ctx={ctx} />
                <NumericCells node={node} ctx={ctx} />
                <td></td>
            </tr>
            {expanded && hasChildren && node.children.map((child) => <RENodeRow key={child.key} node={child} depth={depth + 1} ctx={ctx} />)}
            {expanded && !hasChildren && pagedOut && <PagedOutGroupRow depth={depth + 1} ctx={ctx} />}
            {expanded && isLazy && lazyStatus !== 'error' && <TableRowSkeleton rows={1} columns={ctx.colSpan} />}
            {expanded && isLazy && lazyStatus === 'error' && <LoadErrorRow depth={depth + 1} ctx={ctx} onRetry={() => ctx.requestChildren(node)} />}
        </>
    );
}

/** Inline failure for a lazy fetch (a group's children, search, Expand all), with a retry. */
function LoadErrorRow({ depth = 0, ctx, onRetry }) {
    return (
        <tr className="!tw-bg-white">
            <td colSpan={ctx.colSpan}>
                <div className={classNames('tw-flex tw-items-center tw-gap-x-1.5', indentClass(depth))}>
                    <span className="tw-inline-flex tw-w-4 tw-shrink-0"></span>
                    <span className="tw-text-red">{$t('Failed to load rows.')}</span>
                    <TextButton variant="primary" onClick={onRetry}>
                        {$t('Retry')}
                    </TextButton>
                </div>
            </td>
        </tr>
    );
}

/** Placeholder shown when an expanded group's own lines all sit on another page. */
function PagedOutGroupRow({ depth, ctx }) {
    return (
        <tr className="!tw-bg-white tw-text-charcoal-50">
            <td colSpan={ctx.colSpan}>
                <div className={classNames('tw-flex tw-items-center tw-gap-x-1.5', indentClass(depth))}>
                    <span className="tw-inline-flex tw-w-4 tw-shrink-0"></span>
                    <span className="tw-italic">{$t('No lines from this group on this page')}</span>
                </div>
            </td>
        </tr>
    );
}

function SubRow({ node, depth, ctx }) {
    return (
        <tr className="!tw-bg-white tw-text-charcoal-50">
            {ctx.selectable && <td></td>}
            <NameCell node={node} depth={depth} />
            <MiddleCells ctx={ctx} />
            <QtyCell node={node} ctx={ctx} />
            <NumericCells node={node} ctx={ctx} />
            <TrailingCell node={node} ctx={ctx} showTag={false} />
        </tr>
    );
}

/**
 * One day-assignment of an expense pack (IB-878), rendered from the canonical
 * `sub_row` / `pack_day` children both presenters now emit.
 *
 * The venue view threads selection and a live per-day EventTagCell through
 * PackLineRow — that is where a pack's event tags are actually set. Reached
 * without those props (the event view) the identical row renders read-only.
 */
function PackDayRow({ node, depth, ctx, selected = false, onToggleSelect = null, tagStoreUrl = null, tagAllocations = null, onTagUpdated = null }) {
    const dateKey = node.meta?.date_key;
    const selectable = typeof onToggleSelect === 'function';
    const allocations = tagAllocations ?? node.allocations ?? [];

    return (
        <tr className={classNames('!tw-bg-grey/10 tw-border-t tw-border-grey/30', { '!tw-bg-blue-10': selected })}>
            {ctx.selectable && <td></td>}
            <NameCell
                node={node}
                depth={depth}
                prefix={selectable ? <CheckboxInput aria-label={$t('Select day')} checked={selected} onChange={() => onToggleSelect(dateKey)} /> : null}
                onNameClick={selectable ? () => onToggleSelect(dateKey) : null}
            />
            <MiddleCells ctx={ctx} />
            <QtyCell node={node} ctx={ctx} />
            <NumericCells node={node} ctx={ctx} />
            {tagStoreUrl ? (
                <td>
                    <EventTagCell
                        line={{ id: dateKey, type: 'pack-day', allocations }}
                        storeUrl={tagStoreUrl}
                        sports={ctx.sports}
                        onUpdated={(updated) => onTagUpdated && onTagUpdated(dateKey, updated)}
                        allowClear
                    />
                </td>
            ) : (
                <TrailingCell node={node} ctx={ctx} showTag={false} />
            )}
        </tr>
    );
}

function PlainLineRow({ node, depth, ctx }) {
    return (
        <tr className="!tw-bg-white">
            <SelectCheckbox node={node} ctx={ctx} />
            <NameCell node={node} depth={depth} />
            <MiddleCells ctx={ctx} node={node} showValues />
            <QtyCell node={node} ctx={ctx} />
            <NumericCells node={node} ctx={ctx} />
            <TrailingCell node={node} ctx={ctx} />
        </tr>
    );
}

/**
 * A leaf under the Split layer: its Total row (keeping the event-tag chips and
 * bulk-select checkbox) with two muted, figure-only committed / uncommitted rows
 * beneath — mirroring the cashflow-subrow look, indented with the logical
 * `tw-ps-9` property so it flips correctly under RTL.
 */
function SplitLineRow({ node, depth, ctx }) {
    return (
        <>
            <tr className="!tw-bg-white">
                <SelectCheckbox node={node} ctx={ctx} />
                <NameCell node={node} depth={depth} showBox={!!node.meta?.is_pack} />
                <MiddleCells ctx={ctx} node={node} showValues />
                <QtyCell node={node} ctx={ctx} />
                <NumericCells node={node} ctx={ctx} />
                <TrailingCell node={node} ctx={ctx} />
            </tr>
            {node.committed != null && (
                <>
                    <SplitFigureRow ctx={ctx} label={$t('Committed')} value={node.committed} />
                    <SplitFigureRow ctx={ctx} label={$t('Uncommitted')} value={node.uncommitted} />
                </>
            )}
        </>
    );
}

/** One muted, figure-only committed / uncommitted row synthesised under a Split leaf. */
function SplitFigureRow({ ctx, label, value }) {
    return (
        <tr className="!tw-bg-white tw-text-charcoal-50">
            {ctx.selectable && <td></td>}
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-ps-9">
                    <span className="tw-inline-block tw-w-1.5 tw-h-1.5 tw-shrink-0 tw-rounded-full tw-bg-charcoal-50" />
                    <span>{label}</span>
                </div>
            </td>
            <MiddleCells ctx={ctx} />
            {ctx.showQty && <td className="inbud-numeric-cell"></td>}
            {ctx.showCash && <td className="inbud-numeric-cell">{NO_FIGURE}</td>}
            {ctx.showVik && <td className="inbud-numeric-cell">{NO_FIGURE}</td>}
            <td className="inbud-numeric-cell">{formatFigure(value)}</td>
            <td></td>
        </tr>
    );
}

/** A committed / uncommitted footer row shown under the Split layer. */
function FooterCommitmentRow({ label, value, ctx, canBulkEdit }) {
    return (
        <tr className="!tw-bg-charcoal-75">
            {canBulkEdit && <td></td>}
            <td>{label}</td>
            {ctx.showSource && <td></td>}
            {ctx.showDescription && <td></td>}
            {ctx.showPrice && <td></td>}
            {ctx.showQty && <td></td>}
            {ctx.showCash && <td className="inbud-numeric-cell">{NO_FIGURE}</td>}
            {ctx.showVik && <td className="inbud-numeric-cell">{NO_FIGURE}</td>}
            <td className="inbud-numeric-cell">{formatFigure(value)}</td>
            <td></td>
        </tr>
    );
}

/** A line whose committed / uncommitted sub-rows nest beneath it (template child). */
function ExpandableLineRow({ node, depth, ctx }) {
    const [localExpanded, setLocalExpanded] = useState(ctx.expandKey > 0 ? ctx.forceExpanded : false);

    useEffect(() => {
        if (ctx.expandKey > 0) setLocalExpanded(ctx.forceExpanded);
    }, [ctx.expandKey]);

    const [expanded, toggleExpanded] = useRowExpanded(node, ctx, localExpanded, setLocalExpanded);

    return (
        <>
            <tr className="!tw-bg-white">
                <SelectCheckbox node={node} ctx={ctx} />
                <NameCell node={node} depth={depth} expandable expanded={expanded} onToggle={toggleExpanded} showBox={!!node.meta?.is_pack} />
                <MiddleCells ctx={ctx} node={node} showValues />
                <QtyCell node={node} ctx={ctx} />
                <NumericCells node={node} ctx={ctx} />
                <TrailingCell node={node} ctx={ctx} />
            </tr>
            {expanded && node.children.map((child) => <RENodeRow key={child.key} node={child} depth={depth + 1} ctx={ctx} />)}
        </>
    );
}

/** A simple budget line rendered as one row per distinct price (IB-612). */
function PriceRowsLine({ node, depth, ctx }) {
    const priceRows = node.meta.price_rows;
    const canSelect = ctx.selectable && node.selectable && node.meta?.id != null;
    const checked = canSelect && ctx.isLineSelected ? ctx.isLineSelected(node.meta.allocatable_type, node.meta.id) : false;

    return (
        <>
            {priceRows.map((row, index) => (
                <tr key={`${row.committed ? 'c' : 'u'}-${row.price}-${index}`} className="!tw-bg-white">
                    {ctx.selectable && (
                        <td>
                            {index === 0 && canSelect && (
                                <CheckboxInput
                                    aria-label={$t('Select line')}
                                    checked={checked}
                                    onChange={() => ctx.toggleLine && ctx.toggleLine(node.meta.allocatable_type, node.meta.id)}
                                />
                            )}
                        </td>
                    )}
                    <NameCell node={node} depth={depth} />
                    {ctx.showSource && <td></td>}
                    {ctx.showDescription && <td>{index === 0 ? (node.meta?.description ?? '') : ''}</td>}
                    {ctx.showPrice && <td className="inbud-numeric-cell">{formatFigure(row.price)}</td>}
                    {ctx.showQty && <td className="inbud-numeric-cell">{formatQtyFigure(row.qty)}</td>}
                    {ctx.showCash && <td className="inbud-numeric-cell">{formatFigure(row.cash)}</td>}
                    {ctx.showVik && <td className="inbud-numeric-cell">{formatFigure(row.vik)}</td>}
                    <td className="inbud-numeric-cell">{formatFigure(row.total)}</td>
                    {index === 0 ? <TrailingCell node={node} ctx={ctx} /> : <td></td>}
                </tr>
            ))}
        </>
    );
}

/**
 * A pack line, expandable into its day-assignments with day-level event tagging.
 *
 * IB-878 — the days now arrive as canonical `sub_row` / `pack_day` CHILDREN
 * (they used to be hand-rolled here from `meta.quantities`, which carried no
 * figures), so each one renders through the shared PackDayRow. This row keeps
 * ownership of what is genuinely pack-level: expansion, day selection, and the
 * bulk tag / untag actions.
 */
function PackLineRow({ node, depth, ctx }) {
    const [expanded, setExpanded] = useState(false);
    const [selectedDays, setSelectedDays] = useState([]);
    const [bulkTagging, setBulkTagging] = useState(false);

    const packDays = (node.children || []).filter((child) => child.meta?.sub_row_kind === 'pack_day');

    const [dayAllocations, setDayAllocations] = useState(() => {
        const map = {};
        packDays.forEach((day) => {
            map[day.meta.date_key] = Array.isArray(day.allocations) ? day.allocations : [];
        });
        return map;
    });

    const buildDayTagUrl = (day) => {
        const expenseableId = day.meta?.expenseable_id;
        const dateKey = day.meta?.date_key;
        if (!ctx.dayTagStoreUrl || !node.meta?.pack_id || !expenseableId || !dateKey) return null;
        return ctx.dayTagStoreUrl.replace('__PACK_ID__', node.meta.pack_id).replace('__EXPENSEABLE_ID__', expenseableId).replace('__DATE_KEY__', dateKey);
    };

    const buildBulkTagUrl = (expenseableId) => {
        if (!ctx.dayTagBulkUrl || !node.meta?.pack_id || !expenseableId) return null;
        return ctx.dayTagBulkUrl.replace('__PACK_ID__', node.meta.pack_id).replace('__EXPENSEABLE_ID__', expenseableId);
    };

    const handleDayTagUpdated = (dateKey, updatedAllocations) => {
        setDayAllocations((prev) => ({ ...prev, [dateKey]: Array.isArray(updatedAllocations) ? updatedAllocations : [] }));
        ctx.onAllocationUpdated && ctx.onAllocationUpdated(updatedAllocations);
    };

    const toggleDaySelection = (dateKey) => {
        setSelectedDays((prev) => (prev.includes(dateKey) ? prev.filter((k) => k !== dateKey) : [...prev, dateKey]));
    };

    const allDaysSelected = packDays.length > 0 && selectedDays.length === packDays.length;

    const toggleAllDays = () => {
        setSelectedDays(allDaysSelected ? [] : packDays.map((day) => day.meta.date_key));
    };

    const isDayTagged = (dateKey) => (dayAllocations[dateKey] ?? []).length > 0;

    const handleBulkTag = (sport) => {
        if (!sport || selectedDays.length === 0) return;
        setBulkTagging(true);

        const daysToTag = packDays.filter((day) => selectedDays.includes(day.meta.date_key));
        const expenseableId = daysToTag[0]?.meta?.expenseable_id;
        const bulkUrl = buildBulkTagUrl(expenseableId);

        if (!bulkUrl || daysToTag.length === 0) {
            setBulkTagging(false);
            return;
        }

        const payload = {
            days: daysToTag.map((day) => ({ date_key: day.meta.date_key, allocations: [{ sport_id: sport.id, percentage: 100 }] })),
        };

        axios
            .post(bulkUrl, payload)
            .then(() => {
                const updates = {};
                daysToTag.forEach((day) => {
                    updates[day.meta.date_key] = [{ sport_id: sport.id, sport_name: sport.name, percentage: 100 }];
                });
                setDayAllocations((prev) => ({ ...prev, ...updates }));
                setSelectedDays([]);
                ctx.onAllocationUpdated && ctx.onAllocationUpdated([]);
            })
            .catch(() => window.alert($t('Failed to apply event tags. Please try again.')))
            .finally(() => setBulkTagging(false));
    };

    const handleBulkUntag = () => {
        if (selectedDays.length === 0) return;
        setBulkTagging(true);

        const daysToUntag = packDays.filter((day) => selectedDays.includes(day.meta.date_key) && isDayTagged(day.meta.date_key));

        Promise.all(
            daysToUntag.map((day) => {
                const url = buildDayTagUrl(day);
                return url ? axios.delete(url) : Promise.resolve();
            }),
        )
            .then(() => {
                const updates = {};
                daysToUntag.forEach((day) => {
                    updates[day.meta.date_key] = [];
                });
                setDayAllocations((prev) => ({ ...prev, ...updates }));
                setSelectedDays([]);
                ctx.onAllocationUpdated && ctx.onAllocationUpdated([]);
            })
            .catch(() => window.alert($t('Failed to remove event tags. Please try again.')))
            .finally(() => setBulkTagging(false));
    };

    const selectedWithTags = selectedDays.filter((dk) => isDayTagged(dk));
    const selectedWithoutTags = selectedDays.filter((dk) => !isDayTagged(dk));

    // Decision 2 — once a pack HAS days, the pack-level control is a READ-ONLY
    // summary of the union of its day tags, never an editable tag of its own: a
    // pack's days can each go to a different event, so one pack-level tag cannot
    // express them. An UNDATED pack has no days to expand to, so its pack-level
    // allocation stays the sanctioned basis (decision 5's fallback) and its
    // control stays live — otherwise it could never be tagged at all.
    const taggedPerDay = packDays.length > 0;

    // Weighted by each day's money, exactly as the event view derives a pack's
    // share (Σ day value × day % ÷ pack total). An unweighted per-day average
    // would only reach 100% when every day happens to be tagged, so a partly
    // tagged pack would read as under-allocated against a figure the event view
    // never shows. Falls back to an even day weighting if the cached leaf
    // carries no per-day money.
    const dayWeight = (day) => {
        const total = Number(day.total || 0);
        return Number.isFinite(total) ? Math.abs(total) : 0;
    };
    const packWeightTotal = packDays.reduce((sum, day) => sum + dayWeight(day), 0);
    const weightOf = (day) => (packWeightTotal > 0 ? dayWeight(day) / packWeightTotal : 1 / packDays.length);

    const dayTagSummary = Object.values(
        packDays.reduce((acc, day) => {
            (dayAllocations[day.meta.date_key] ?? []).forEach((allocation) => {
                const entry = acc[allocation.sport_id] || {
                    sport_id: allocation.sport_id,
                    sport_name: allocation.sport_name,
                    percentage: 0,
                };
                entry.percentage += Number(allocation.percentage || 0) * weightOf(day);
                acc[allocation.sport_id] = entry;
            });
            return acc;
        }, {}),
    ).map((entry) => ({ ...entry, percentage: Math.round(entry.percentage * 100) / 100 }));

    const canTag = node.meta?.id && ctx.sportAllocationStoreUrl;

    // Day expansion (and the live per-day tag control it hosts) is a Total-layer
    // view — Committed / Uncommitted / Split re-base a pack to a single figure
    // like any other line under those layers. The pack's OWN Events cell above
    // stays exactly as it is in every layer (day tags are always the basis
    // regardless of which commitment figure is showing).
    const canExpandDays = !ctx.commitmentLayerEnabled || ctx.layer === COMMITMENT_LAYERS.TOTAL;
    const showExpanded = canExpandDays && expanded;

    return (
        <>
            <tr className="!tw-bg-white">
                <SelectCheckbox node={node} ctx={ctx} />
                <NameCell
                    node={node}
                    depth={depth}
                    expandable={canExpandDays}
                    expanded={showExpanded}
                    onToggle={canExpandDays ? () => setExpanded((s) => !s) : null}
                    showBox
                />
                <MiddleCells ctx={ctx} node={node} showValues />
                <QtyCell node={node} ctx={ctx} />
                <NumericCells node={node} ctx={ctx} />
                <td>
                    {canTag ? (
                        <EventTagCell
                            line={{
                                id: node.meta.id,
                                type: node.meta.allocatable_type,
                                allocations: taggedPerDay ? dayTagSummary : node.allocations || [],
                            }}
                            sports={ctx.sports}
                            storeUrl={ctx.sportAllocationStoreUrl}
                            onUpdated={ctx.onAllocationUpdated}
                            readonly={taggedPerDay || node.meta?.readonly === true}
                            venueId={ctx.venueId}
                            allowClear={!taggedPerDay}
                            isSummary={taggedPerDay}
                            // Passed as a `note` rather than rendered below the cell: a second
                            // line here made every per-day-tagged pack row double height, the
                            // same complaint as the wrapping chips (IB-875).
                            note={taggedPerDay ? $t('Tagged per day — expand to edit') : null}
                        />
                    ) : null}
                </td>
            </tr>

            {showExpanded && packDays.length > 0 && (
                <>
                    {selectedDays.length > 0 && (
                        <tr className="!tw-bg-blue-10 tw-border-t tw-border-blue-15">
                            <td colSpan={ctx.colSpan} className="tw-pe-4 tw-py-2">
                                <div className={classNames('tw-flex tw-items-center tw-gap-x-3 tw-text-sm', indentClass(depth + 1))}>
                                    <span className="tw-font-medium tw-text-blue">{$t('{{count}} days selected', { count: selectedDays.length })}</span>
                                    {selectedWithoutTags.length > 0 && (
                                        <div className="tw-flex tw-items-center tw-gap-x-1">
                                            <span className="tw-text-charcoal-75">{$t('Tag with:')}</span>
                                            {ctx.sports.map((sport) => (
                                                <button
                                                    key={sport.id}
                                                    type="button"
                                                    disabled={bulkTagging}
                                                    onClick={() => handleBulkTag(sport)}
                                                    className="tw-rounded-full tw-bg-blue tw-px-2 tw-py-0.5 tw-text-xs tw-text-white hover:tw-bg-blue-50 disabled:tw-opacity-50"
                                                >
                                                    {sport.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {selectedWithTags.length > 0 && (
                                        <TextButton variant="danger" disabled={bulkTagging} onClick={handleBulkUntag}>
                                            {$t('Clear tags from {{count}} days', { count: selectedWithTags.length })}
                                        </TextButton>
                                    )}
                                    <TextButton variant="secondary" onClick={() => setSelectedDays([])}>
                                        {$t('Cancel')}
                                    </TextButton>
                                </div>
                            </td>
                        </tr>
                    )}

                    <tr className="!tw-bg-grey/30 tw-border-t tw-border-grey">
                        <td colSpan={ctx.colSpan} className="tw-pe-4 tw-py-1">
                            <div className={indentClass(depth + 1)}>
                                <CheckboxInput label={$t('Select all days')} checked={allDaysSelected} onChange={toggleAllDays} />
                            </div>
                        </td>
                    </tr>

                    {packDays.map((day) => (
                        <PackDayRow
                            key={day.key}
                            node={day}
                            depth={depth + 1}
                            ctx={ctx}
                            selected={selectedDays.includes(day.meta.date_key)}
                            onToggleSelect={toggleDaySelection}
                            tagStoreUrl={buildDayTagUrl(day)}
                            tagAllocations={dayAllocations[day.meta.date_key] ?? []}
                            onTagUpdated={handleDayTagUpdated}
                        />
                    ))}
                </>
            )}
        </>
    );
}
